/* eslint-disable no-nested-ternary */
import React from 'react';
import classNameBind from 'classnames/bind';
import { connect, ConnectedProps } from 'react-redux';
import { flushSync } from 'react-dom';
import _uniqBy from 'lodash/uniqBy';
import { TFunction } from 'react-i18next';
import { RouterService } from '@piwikpro/router-crate';
import { DebouncedFunc, debounce } from 'lodash';
import {
  OverlayView,
  View,
  Transfer,
  Button,
  Text,
  Stack,
  withTranslation,
} from '@piwikpro/ui-components';
import {
  view,
  errorBoundary,
  withInject,
  ConfigService,
} from '@piwikpro/platform';
import TitleComponent from '@piwikpro/title-crate/Title';
import { FormValidator } from '@piwikpro/form-crate';
import { MetaSites } from '@piwikpro/meta-sites-crate';
import { MetaSitesState } from '@piwikpro/meta-sites-crate/reducers';
import { UnsavedChangesTracker as UnsavedChangesTrackerComponent } from '@piwikpro/form-crate/components/UnsavedChangesTracker';
import styles from './AddApps.module.css';

const bindClasses = classNameBind.bind(styles);

const ASCENDING = '';
const DESCENDING = '-';
const CNIL_SORT_OPTION = 'cnil';

const mapStateToProps = (state: MetaSitesState) => ({
  metaSite: state.metaSites.details.settings.settings,
  outsideApps: state.metaSites.apps.outside.list,
  total: state.metaSites.apps.outside.total,
  isLoading: state.metaSites.apps.outside.isLoading,
  failed: state.metaSites.apps.outside.failed,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  config: ConfigService
  metaSites: MetaSites
  validation: FormValidator
  Title: typeof TitleComponent
  UnsavedChangesTracker: typeof UnsavedChangesTrackerComponent
  routerService: RouterService
  t: TFunction
}

interface Props extends InjectedProps, ReduxProps {
  appsInsidePaginationLimit: number
}

interface Element {
  id: string
  name: string
  disabledTooltipContent?: string
  isSelected?: boolean
  label?: React.ReactNode | string
  cnil: boolean
}

interface State {
  limit: number
  from: {
    offset: number
    elements: Element[]
    search: string
    searchDebounce: DebouncedFunc<any> | null
    checkedElements: Element[]
    sortBy: string
    total: number
  }
  to: {
    offset: number
    elements: Element[]
    sortBy: string
    search: string
  }
  formIsLoading: boolean
}

class AddApps extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      limit: 10,
      from: {
        offset: 0,
        elements: [],
        search: '',
        searchDebounce: null,
        sortBy: CNIL_SORT_OPTION,
        checkedElements: [],
        total: 0,
      },
      to: {
        offset: 0,
        elements: [],
        sortBy: CNIL_SORT_OPTION,
        search: '',
      },
      formIsLoading: false,
    };

    this.handleClose = this.handleClose.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.getListFromBackend = this.getListFromBackend.bind(this);
  }

  componentDidMount() {
    this.getListFromBackend();
  }

  async getListFromBackend() {
    const { from, limit } = this.state;
    const { metaSites, metaSite } = this.props;

    try {
      await metaSites.fetchAppsOutsideMetaSite(metaSite.id, {
        search: from.search,
        offset: from.offset,
        sort: {
          by: from.sortBy.replace('-', ''),
          direction: from.sortBy.includes(DESCENDING) ? DESCENDING : ASCENDING,
        },
        limit,
      });
      // eslint-disable-next-line no-empty
    } catch (err) { }
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    const { outsideApps, total, t } = this.props;
    const { from, to } = this.state;

    if (prevProps.outsideApps !== outsideApps) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        from: {
          ...from,
          elements: outsideApps.map(outsideApp => (
            {
              id: outsideApp.id,
              name: outsideApp.attributes.name,
              disabledTooltipContent: to.elements.some(toElement => toElement.id === outsideApp.id) ? t('meta-sites.addApps.list-element.disabled-tooltip-text') : '',
              label: outsideApp.attributes.cnil ? (
                <Text as="p" weight={600}>
                  {t('meta-sites.addApps.checkboxList.column.cnil.on')}
                </Text>
              ) : (
                <Text as="p" color="InkLighter">
                  {t('meta-sites.addApps.checkboxList.column.cnil.off')}
                </Text>
              ),
              cnil: outsideApp.attributes.cnil,
            }
          )),
          total,
        },
      });
    }
  }

  handleClose() {
    const { routerService } = this.props;

    routerService.dismissAdministrationOverlay();
  }

  async handleSubmit() {
    const {
      metaSite,
      metaSites,
      appsInsidePaginationLimit,
    } = this.props;
    const { to } = this.state;

    this.setState({
      formIsLoading: true,
    });

    try {
      await metaSites.addAppsToMetaSite(metaSite.id, to.elements.map(app => app.id));

      this.handleClose();
      await metaSites.fetchMetaSiteSettings(metaSite.id);
      await metaSites.fetchAppsInsideMetaSite(metaSite.id, {
        search: '',
        limit: appsInsidePaginationLimit,
        offset: 0,
        sort: {
          by: CNIL_SORT_OPTION,
          direction: ASCENDING,
        },
      });
      await metaSites.fetchMetaSiteIntegrity(metaSite.id);
      // eslint-disable-next-line no-empty
    } catch { } finally {
      this.setState({
        formIsLoading: false,
      });
    }
  }

  render() {
    const {
      metaSite,
      failed,
      isLoading,
      validation,
      t,
      config,
      Title,
      UnsavedChangesTracker,
    } = this.props;
    const {
      from, to, limit, formIsLoading,
    } = this.state;

    const elementsToSortedAndFiltered = to.elements
      .filter(el => el.name?.toLowerCase().indexOf(to.search.toLowerCase()) > -1)
      // @ts-ignore
      .sort((a, b) => {
        if (to.sortBy === 'cnil') {
          const cnilA = a.cnil;
          const cnilB = b.cnil;

          return (cnilA === cnilB) ? 0 : cnilA ? 1 : -1;
        } if (to.sortBy === '-cnil') {
          const cnilA = a.cnil;
          const cnilB = b.cnil;

          return (cnilA === cnilB) ? 0 : cnilA ? -1 : 1;
        }

        return 0;
      });

    const getVisibleCheckedElementsTo = list => list
      .slice(to.offset, to.offset + limit)
      .filter(el => el.isSelected);

    const areAllVisibleElementsToChecked = getVisibleCheckedElementsTo(elementsToSortedAndFiltered)
      .length === elementsToSortedAndFiltered
      .slice(to.offset, to.offset + limit)
      .filter(el => !el.disabledTooltipContent).length;

    const visibleCheckedElementsFrom = from.elements
      .filter(el => from.checkedElements.findIndex(el2 => el.id === el2.id) > -1);

    const isEveryPossibleElementToSelected = elementsToSortedAndFiltered.length
      && elementsToSortedAndFiltered.length === elementsToSortedAndFiltered
        .filter(el => el.isSelected).length;
    const allFromElementsPossibleToSelectLength = from.total - to.elements.length;

    return (
      <Title
        part={t('meta-sites.addApps.title', { metaSiteName: metaSite.attributes.name })}
        subset
      >
        <UnsavedChangesTracker
          discardAction={this.handleClose}
          type="twoOptions"
          isModified={to.elements.length > 0 && !formIsLoading}
          render={(api) => {
            const { UnsavedChangesModal, onClose } = api;
            return (
              <OverlayView
                isOpen
                onClose={onClose}
                title={t('meta-sites.addApps.title', { metaSiteName: metaSite.attributes.name })}
              >
                <View heightAuto>
                  <div className={bindClasses('overlay-wrapper')}>
                    <View.Body>
                      {/* this component code is mostly moved and adjusted from component story:
                      https://github.com/PiwikPRO/PPMS-UIComponents/blob/master/storybook/lib/components/TransferDemos/TransferDemoWithBackendPagination/TransferDemoWithBackendPagination.jsx
                  */}
                      <Transfer
                        from={{
                          title: t('meta-sites.addApps.checkboxList.leftHeader'),
                          failed,
                          selected: {
                            checkbox: {
                              checked: !!visibleCheckedElementsFrom.length,
                              disabled: from.elements.every(el => el.disabledTooltipContent),
                              checkedMixed: from.elements
                                .filter(el => !el.disabledTooltipContent).length
                              !== visibleCheckedElementsFrom.length,
                              onChange: ({ target: { checked } }) => {
                                this.setState({
                                  from: {
                                    ...from,
                                    checkedElements: checked
                                      ? [
                                        ...from.checkedElements,
                                        ...from.elements.filter(
                                          element => !element.disabledTooltipContent,
                                        )]
                                      : from.checkedElements
                                        .filter(
                                          el => !visibleCheckedElementsFrom
                                            .some(
                                              elCheckedVisible => el.id === elCheckedVisible.id,
                                            ),
                                        ),
                                  },
                                });
                              },
                            },
                            count: from.checkedElements.length,
                            total: allFromElementsPossibleToSelectLength < 0
                              ? from.total
                              : allFromElementsPossibleToSelectLength,
                          },
                          search: {
                            value: from.search,
                            onChange: ({ target: { value } }) => {
                              const { from: { searchDebounce } } = this.state;
                              if (searchDebounce) {
                                searchDebounce.cancel();
                              }

                              const handleSearch = debounce(() => {
                                this.getListFromBackend();
                              }, config.get('LIST_SEARCH_DELAY'));

                              this.setState({
                                from: {
                                  ...from,
                                  searchDebounce: handleSearch,
                                  search: value,
                                  offset: 0,
                                },
                              });

                              handleSearch();
                            },
                          },
                          listHeaders: {
                            nameHeader: {
                              name: t('meta-sites.addApps.checkboxList.column.header.apps'),
                            },
                            labelHeader: {
                              name: t('meta-sites.addApps.checkboxList.column.header.cnil'),
                              onClick: () => {
                                flushSync(() => {
                                  this.setState({
                                    from: {
                                      ...from,
                                      sortBy: from.sortBy === CNIL_SORT_OPTION
                                        ? DESCENDING + CNIL_SORT_OPTION
                                        : ASCENDING + CNIL_SORT_OPTION,
                                    },
                                  });
                                });
                                this.getListFromBackend();
                              },
                              sortByOrder: !from.sortBy.includes(CNIL_SORT_OPTION) ? undefined : from.sortBy.includes(DESCENDING) ? 'dsc' : 'asc',
                            },
                          },
                          list: from.elements
                            .map(el => ({
                              ...el,
                              isSelected: from.checkedElements
                                .findIndex(el2 => el.id === el2.id) > -1,
                            })),
                          onItemSelect: (e, id) => {
                            this.setState({
                              from: {
                                ...from,
                                checkedElements: from.checkedElements
                                  .findIndex(el => el.id === id) > -1
                                  ? from.checkedElements.filter(el => el.id !== id)
                                  : [
                                    ...from.checkedElements,
                                    from.elements.find(el => el.id === id)!,
                                  ],
                              },
                            });
                          },
                          pagination: {
                            onPageChange: (v) => {
                              flushSync(() => {
                                this.setState({ from: { ...from, offset: v } });
                              });
                              this.getListFromBackend();
                            },
                            total: from.total,
                            startIndex: from.offset,
                            endIndex: from.offset + limit,
                            limitOptions: config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS'),
                          },
                          isLoading,
                        }}
                        to={{
                          title: t('meta-sites.addApps.checkboxList.rightHeader'),
                          errors: (to.elements.some(incomingApp => incomingApp.cnil) && [
                            { type: 'warning', content: t('meta-sites.addApps.checkboxList.right.information.text') },
                          ]) || [],
                          selected: {
                            checkbox: {
                              checked: !!getVisibleCheckedElementsTo(
                                elementsToSortedAndFiltered,
                              ).length,
                              checkedMixed: !areAllVisibleElementsToChecked,
                              disabled: elementsToSortedAndFiltered.length === 0,
                              onChange: () => {
                                const idsToSelect = elementsToSortedAndFiltered
                                  .slice(to.offset, to.offset + limit).map(el => el.id);
                                this.setState({
                                  to: {
                                    ...to,
                                    elements: to.elements.map(
                                      el => (idsToSelect.includes(el.id)
                                      && !el.disabledTooltipContent
                                        ? {
                                          ...el,
                                          isSelected: !getVisibleCheckedElementsTo(
                                            elementsToSortedAndFiltered,
                                          ).length,
                                        }
                                        : el),
                                    ),
                                  },
                                });
                              },
                            },
                            count: to.elements.filter(el => el.isSelected).length,
                            total: elementsToSortedAndFiltered.length,
                          },
                          search: {
                            value: to.search,
                            onChange: ({ target: { value } }) => {
                              this.setState({
                                to: { ...to, search: value, offset: 0 },
                              });
                            },
                          },
                          listHeaders: {
                            nameHeader: {
                              name: t('meta-sites.addApps.checkboxList.column.header.apps'),
                            },
                            labelHeader: {
                              name: t('meta-sites.addApps.checkboxList.column.header.cnil'),
                              onClick: () => {
                                this.setState({
                                  to: {
                                    ...to,
                                    sortBy: to.sortBy === CNIL_SORT_OPTION
                                      ? DESCENDING + CNIL_SORT_OPTION
                                      : ASCENDING + CNIL_SORT_OPTION,
                                  },
                                });
                              },
                              sortByOrder: !to.sortBy.includes(CNIL_SORT_OPTION) ? undefined : to.sortBy.includes(DESCENDING) ? 'dsc' : 'asc',
                            },
                          },
                          list: elementsToSortedAndFiltered.slice(to.offset, to.offset + limit),
                          onItemSelect: (e, id) => {
                            this.setState({
                              to: {
                                ...to,
                                elements: to.elements
                                  .map(el => (el.id === id ? {
                                    ...el,
                                    isSelected: !el.isSelected,
                                  } : el)),
                              },
                            });
                          },
                          pagination: {
                            onPageChange: (v) => { this.setState({ to: { ...to, offset: v } }); },
                            total: elementsToSortedAndFiltered.length,
                            startIndex: to.offset,
                            endIndex: to.offset + limit,
                            limitOptions: config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS'),
                          },
                          onSelectAll: elementsToSortedAndFiltered.length > 0 ? () => {
                            const idsToSelect = elementsToSortedAndFiltered.map(el => el.id);
                            this.setState({
                              to: {
                                ...to,
                                elements: to.elements.map(
                                  el => (
                                    idsToSelect.includes(el.id)
                                      ? {
                                        ...el,
                                        isSelected: !isEveryPossibleElementToSelected,
                                      } : el),
                                ),
                              },
                            });
                          } : undefined,
                          deselectAllButtonMode: Boolean(isEveryPossibleElementToSelected),
                        }}
                        limit={limit}
                        onLimitChange={(v) => {
                          flushSync(() => {
                            this.setState({
                              limit: v,
                              from: { ...from, offset: 0 },
                              to: { ...to, offset: 0 },
                            });
                          });
                          this.getListFromBackend();
                        }}
                        onMoveItemToRight={
                          (draggedEl) => {
                            this.setState(({ to: latestTo, from: latestFrom }) => {
                              const { elements: fromElements, checkedElements } = latestFrom;
                              const { elements: toElements } = latestTo;

                              const foundElement = fromElements.find(
                                ({ id }) => draggedEl.id === id,
                              );

                              const allElements = [
                                ...toElements,
                                ...checkedElements,
                                ...(foundElement ? [foundElement] : []),
                              ];

                              return {
                                to: {
                                  ...latestTo,
                                  elements: _uniqBy(allElements, 'id'),
                                },
                                from: {
                                  ...latestFrom,
                                  checkedElements: [],
                                },
                              };
                            });
                            this.getListFromBackend();
                          }
                        }
                        onMoveItemToLeft={
                          (draggedEl) => {
                            this.setState(({ to: latestTo }) => {
                              const filteredItems = latestTo.elements
                                .filter(el => !el.isSelected && el.id !== draggedEl.id);
                              const currentPage = latestTo.offset;
                              const lastPage = filteredItems.length > limit
                                ? (Math.ceil(filteredItems.length / limit) - 1) * limit
                                : 0;

                              return {
                                to: {
                                  ...latestTo,
                                  elements: filteredItems,
                                  offset: currentPage > lastPage ? lastPage : currentPage,
                                },
                              };
                            });
                            this.getListFromBackend();
                          }
                        }
                      />
                    </View.Body>
                  </div>
                  <View.Action isFloating>
                    <Stack horizontalAlignment="right" fullWidth spacing="narrow">
                      <Stack.Item>
                        <Button onClick={onClose} text={t('components:cancel')} disabled={formIsLoading} />
                      </Stack.Item>
                      <Stack.Item>
                        <Button
                          appearance="primary"
                          text={t('meta-sites.addApps.button.submit')}
                          disabled={!validation.isValueValid(to.elements, 'metaSites.apps')}
                          pending={formIsLoading}
                          onClick={this.handleSubmit}
                        />
                      </Stack.Item>
                      <UnsavedChangesModal />
                    </Stack>
                  </View.Action>
                </View>
              </OverlayView>
            );
          }}
        />
      </Title>
    );
  }
}

export default connector(withInject<InjectedProps>({
  config: 'config',
  validation: 'FormCrate.validation',
  metaSites: 'MetaSitesCrate.metaSites',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
  UnsavedChangesTracker: 'FormCrate.components.UnsavedChangesTracker',
})(
  view<Props, InjectedProps & ReduxProps>({ role: 'OWNER' }, false)(errorBoundary(withTranslation(['administration'])(AddApps))),
));
